'use client';

import type { Control, FieldValues, Path } from 'react-hook-form';
import { FormInput } from '@/components/forms/form-input';
import { FormTextarea } from '@/components/forms/form-textarea';
import { FormDatePicker } from '@/components/forms/form-date-picker';
import { FormSelect } from '@/components/forms/form-select';
import { FormSwitch } from '@/components/forms/form-switch';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group';
import { FormPhoneInput } from '@/components/forms/form-phone-input';
import type { DynamicFormField } from '../types';

interface DynamicFormFieldInputProps<T extends FieldValues> {
  field: DynamicFormField;
  control: Control<T>;
  name: Path<T>;
}

function DynamicFormFieldInput<T extends FieldValues>({
  field,
  control,
  name
}: DynamicFormFieldInputProps<T>) {
  const label = field.label;
  const required = field.is_required;

  switch (field.type) {
    case 'short_text':
      return (
        <FormInput
          control={control}
          name={name}
          label={label}
          placeholder={label}
          required={required}
        />
      );

    case 'long_text':
      return (
        <FormTextarea
          control={control}
          name={name}
          label={label}
          placeholder={label}
          required={required}
        />
      );

    case 'number':
      return (
        <FormInput
          control={control}
          name={name}
          label={label}
          placeholder='0'
          type='number'
          required={required}
        />
      );

    case 'phone':
      return (
        <FormPhoneInput
          control={control}
          name={name}
          label={label}
          placeholder={label}
          required={required}
        />
      );

    case 'email':
      return (
        <FormInput
          control={control}
          name={name}
          label={label}
          placeholder='email@example.com'
          type='email'
          required={required}
        />
      );

    case 'date':
      return (
        <FormDatePicker
          control={control}
          name={name}
          label={label}
          config={{
            placeholder: label,
            captionLayout: 'dropdown',
            startMonth: new Date(1900, 0),
            endMonth: new Date(2100, 11)
          }}
          required={required}
        />
      );

    case 'selection_list': {
      const options = (field.options ?? [])
        .filter((o) => typeof o === 'string' && o.trim())
        .map((o) => ({ value: o, label: o }));

      if (field.is_multi_select) {
        return (
          <FormCheckboxGroup
            control={control}
            name={name}
            label={label}
            options={options}
            required={required}
            columns={1}
          />
        );
      }

      return (
        <FormSelect
          control={control}
          name={name}
          label={label}
          placeholder={label}
          options={options}
          required={required}
        />
      );
    }

    case 'yes_no':
      return (
        <FormSwitch
          control={control}
          name={name}
          label={label}
          required={required}
        />
      );

    default:
      return null;
  }
}

export { DynamicFormFieldInput };
